import { useMemo } from 'react';

import { InviteClientHeader } from '@/components/invite-client/InviteClientHeader';

import { Link2, Mail, UserPlus } from 'lucide-react';

import {
  useInviteClientContext,
  type StepType,
} from '@/components/invite-client/InviteClientContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

const allMethods = [
  {
    icon: UserPlus,
    type: 'quick-intake',
    title: 'Fill Intake Form for Client',
    description:
      'Add a new client and complete the intake form on their behalf. No invitation will be sent.',
    requiresFormProvider: true,
  },
  {
    icon: Mail,
    type: 'email',
    title: 'Send via Email',
    description:
      'Send a personalized email invitation to clients with a direct link to start their long-term care assessment.',
    requiresFormProvider: false,
  },
  {
    icon: Link2,
    type: 'link',
    title: 'Copy Shareable Link',
    description:
      'Get a unique link that you can share with clients through text, social media, or in person',
    requiresFormProvider: false,
  },
];

export function InviteClientMethod() {
  const { handleStepChange } = useInviteClientContext();
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;

  const methods = useMemo(
    () => allMethods.filter(method => !method.requiresFormProvider || useFormProvider),
    [useFormProvider],
  );

  return (
    <>
      <InviteClientHeader
        title="How would you like to invite your client?"
        description="Invite your client in the way that fits your process seamlessly."
      />
      <div className="mt-4 flex flex-col gap-4">
        {methods.map(({ type, icon: Icon, title, description }) => (
          <button
            key={type}
            type="button"
            onClick={() => handleStepChange(type as StepType)}
            className="hover:border-primary/20 hover:bg-primary/5 flex w-full items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-all"
          >
            <div className="bg-primary/10 flex size-12 shrink-0 items-center justify-center rounded-md">
              <span className="text-primary">
                <Icon className="size-5" />
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-base font-semibold text-gray-900">{title}</span>
              <span className="text-sm text-gray-700">{description}</span>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}
